<template>
  <AppWithLoading v-slot:default="{loader, loading}">
    <VRow justify="center">
      <VCol v-if="loading || localLoading" cols="12">
        <AppPreloader />
      </VCol>
      <VCol v-else cols="12">
        <template v-if="!testStarted">
          <div class="testing d-flex flex-column align-start">
            <img :src="require('../../assets/img/test/start-test.svg')">

            <div class="testing__title">
              {{ $t('startTestingTitle.1') }}<span
                v-if="timer"
              > {{ $t('startTestingTitle.2', {minutes: timer}) }}
              </span>
            </div>
            <div class="testing__subtitle">
              <div>
                {{ $t('startTestingSubtitle.1') }}
              </div>
              {{ $t('startTestingSubtitle.2') }}
            </div>

            <VBtn color="success" class="px-4 mt-7 center" @click="startTest()">
              {{ $t('startTest') }}
            </VBtn>
          </div>
        </template>
        <template v-else-if="testStarted && !testFinished">
          <div class="pa-2">
            <div class="interview-testing__header">
              <div class="interview-testing__header_title">
                {{ $t('testing') }}
              </div>

              <div v-if="timer" class="interview-testing__header_timer">
                {{ $t('timeToComplete') + `: ${timer} ` + $t('minutes') }}
              </div>
            </div>
            <VForm>
              <TestForm
                :questions="testInfo.questions"
                :answers="testAnswers"
                :disabled-answers="disabledAnswers"
                @finishTest="finishTest"
                @openDialog="openWarningFinishDialog"
              />
            </VForm>
          </div>
        </template>

        <template v-else-if="testFinished">
          <div class="testing">
            <template v-if="testTimesUp">
              <img :src="require('../../assets/img/test/test-times-up.svg')">
            </template>

            <template v-else>
              <img :src="require('../../assets/img/test/thank-you-test.svg')">
            </template>

            <div class="testing__title">
              <template v-if="testTimesUp">
                {{ $t('testingIsCompleted') }}.
              </template>
              <template v-else>
                <div>{{ $t('thanks') }}</div>
                <div v-if="testPassed">
                  {{ $t('finishedTestingTitle') }}
                </div>
              </template>
            </div>
            <div class="testing__subtitle">
              <div>
                <template v-if="testTimesUp">
                  {{ $t('finishedTestingSubtitle.4') }}
                </template>
                <template v-else>
                  {{ $t('finishedTestingSubtitle.1') }}
                </template>
              </div>
              <div>
                <template v-if="testTimesUp">
                  {{ $t('finishedTestingSubtitle.5') }}
                </template>
                <template v-else-if="!testPassed && !testTimesUp">
                  {{ $t('finishedTestingSubtitle.3') }}
                </template>
                <template v-else>
                  {{ $t('finishedTestingSubtitle.2') }}
                </template>
              </div>
              <div v-if="testPassed">
                <div v-if="testTimesUp">
                  {{ $t('finishedTestingSubtitle.2') }}
                </div>
                <div>
                  {{ $t('finishedTestingSubtitle.6') }}
                </div>
              </div>
            </div>

            <VBtn
              v-if="testPassed"
              color="success"
              class="px-4 mt-7 center"
              @click="loader(() => startTheInterview())"
            >
              {{ $t('startInterview') }}
            </VBtn>
          </div>
        </template>
      </VCol>
    </VRow>

    <Modal
      :modal-active="dialogWarningFinishTesting"
      :show-buttons="true"
      @close="closeWarningFinishDialog"
      @accept="finishTest"
    >
      <template #header>
        <div class="finish-dialog__inner">
          <img :src="require('../../assets/img/test/warning.svg')">
          <div class="textDialog">
            {{ $t('textDialog') }}
          </div>
        </div>
      </template>
      <template #button-ok>
        {{ $t('finishTest') }}
      </template>
    </Modal>
  </AppWithLoading>
</template>

<script>
import {
  ref, computed, toRefs, onMounted, onUpdated, nextTick,
} from '@vue/composition-api';
import TestForm from './TestForm.vue';
import AppWithLoading from '../AppWithLoading.vue';
import AppPreloader from '../AppPreloader.vue';
import store from '../../store/store';
import Modal from '../Modal.vue';

export default {
  name: 'Test',
  components: {
    AppWithLoading,
    TestForm,
    AppPreloader,
    Modal,
  },
  props: {
    candidate: {
      type: Object,
      required: true,
    },
    vacancy: {
      type: Object,
      required: true,
    },
    id: {
      type: Number,
      required: true,
    },
    testStarted: {
      type: Boolean,
      required: true,
    },
    testInfo: {
      type: Object,
      required: false,
      default: null,
    },
    testFinished: {
      type: Boolean,
      required: true,
    },
    testPassed: {
      type: Boolean,
      required: true,
    },
    disabledAnswers: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  setup(props, { emit }) {
    const { testInfo, candidate, vacancy } = toRefs(props);
    const dialogWarningFinishTesting = ref(false);
    const testAnswers = ref([]);
    const localLoading = ref(true);

    if (!vacancy.value.hasTest) {
      emit('done');
    }

    const timer = computed(() => testInfo.value?.timer);
    const testTimesUp = computed(() => (new Date(candidate.value?.testFinishedAt)
      .getTime() - new Date(candidate.value?.testStartedAt).getTime()) === timer.value * 60000);

    function startTest() {
      emit('start');
    }

    function openWarningFinishDialog() {
      dialogWarningFinishTesting.value = true;
    }

    function closeWarningFinishDialog() {
      dialogWarningFinishTesting.value = false;
    }

    async function finishTest() {
      emit('finish');
    }

    function startTheInterview() {
      emit('done');
    }

    async function fetchAnswers() {
      testAnswers.value = await store.dispatch('candidate/getCandidateTestAnswers');
    }

    onMounted(async () => {
      localLoading.value = true;

      await fetchAnswers();
      localLoading.value = false;

      nextTick(() => {
        const imgs = document.images;
        const len = imgs.length;
        let counter = 0;

        function incrementCounter() {
          counter++;
          if (counter === len) {
            emit('heightChanged');
          }
        }

        [].forEach.call(imgs, (img) => {
          if (img.complete) incrementCounter();
          else img.addEventListener('load', incrementCounter, false);
        });
      });

      emit('heightChanged');
    });

    onUpdated(async () => {
      emit('heightChanged');
    });

    return {
      dialogWarningFinishTesting,
      timer,
      testTimesUp,
      startTest,
      openWarningFinishDialog,
      closeWarningFinishDialog,
      finishTest,
      startTheInterview,
      testAnswers,
      localLoading,
    };
  },
};
</script>

<style lang="scss" scoped>
.interview-testing {
  &__header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    color: #212121;
    font-weight: 400;

    &_title {
      font-size: 24px;
      line-height: 28px;
    }

    &_timer {
      color: #333333;
      font-size: 14px;
      line-height: 16.8px;
    }
  }
}

.testing {
  img {
    margin-bottom: 20px;
  }

  text-align: start;

  &__title {
    font-size: 24px;
    line-height: 28.8px;
    font-weight: 400;
    margin-bottom: 15px;

    @media screen and (max-width: 428px) {
      font-size: 20px;
      line-height: 22px;
    }
  }

  &__subtitle {
    font-size: 14px;
    font-weight: 400;
    line-height: 20px;
  }
}

.textDialog {
  font-size: 16px;
  font-weight: 400;
  line-height: 20px;
  margin-top: 10px;
}

.close-button {
  position: absolute;
  right: 15px;
  top: 15px;
}

.finish-dialog {
  &__inner {
    background-color: #fff;
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 30px 30px 0;
    flex-direction: column;
    img {
      width: 45px;
    }
  }
}

@media #{map-get($display-breakpoints, 'sm-and-down')} {
  .interview-testing__header_timer {
    display: none;
  }
}
</style>

<i18n locale="en" lang="yaml">
  testing: Testing
  finishTest: Finish the testing section
  timeToComplete: Time to complete
  startTest: Start the test
  startInterview: Start a video interview
  testingIsCompleted: The testing section is over.
  startTestingTitle: {
    1: 'The first step of the interview is testing.',
    2: 'You will have {minutes} min to complete it.'
  }
  startTestingSubtitle: {
    1: 'Please be ready for the start of the testing and remember that it will not be possible to pause the test once you have begun.',
    2: 'If you don’t finish in time, all the questions that you did answer in the allotted time will be sent to the employer.'
  }
  minutes: min
  thanks: Thanks!
  finishedTestingTitle: You have passed the testing section.
  finishedTestingSubtitle: {
    1: 'Your results have been sent to the employer.',
    2: 'The next step in the selection process is a video interview.',
    3: 'We appreciate your interest in our company and your participation in the testing.',
    4: 'Unfortunately, you were unable to finish the assignment in the given time.',
    5: 'All the answers that you have completed have been sent to the employer for evaluation.',
    6: 'To take part in the interview, you will need a smartphone or a computer with a web-camera.'
  }
  requiredField: This field is required
  textDialog: You haven't answered all the questions. Are you sure you want to finish testing?
</i18n>

<i18n locale="ru" lang="yaml">
  testing: Тестирование
  finishTest: Закончить тестирование
  timeToComplete: Время для прохождения
  startTest: Начать тестирование
  startInterview: Начать видео-интервью
  testingIsCompleted: Тестирование завершено
  startTestingTitle: {
    1: 'Первый этап собеседования — тестирование',
    2: ', для которого выделено {minutes} мин.'
  }
  startTestingSubtitle: {
    1: 'Пожалуйста, будьте готовы к началу тестирования и учитывайте, что пауза во время выполнения теста не предусмотрена.',
    2: 'Если вы не успеете завершить тест в отведенное время, работодателю будут отправлены те ответы, которые были заполнены до истечения времени.'
  }
  minutes: мин.
  thanks: Спасибо!
  finishedTestingTitle: Вы прошли тестирование.
  finishedTestingSubtitle: {
    1: 'Результаты тестирования переданы работодателю.',
    2: 'Следующим шагом в процессе отбора является видео-интервью.',
    3: 'Благодарим вас за ваш интерес к нашей компании и ваше участие в тестировании.',
    4: 'К сожалению, вы не успели завершить все задания в отведенное время.',
    5: 'Все ответы, которые вы успели заполнить, переданы работодателю для оценки.',
    6: 'Для прохождения видео-интервью вам понадобится смартфон или компьютер с веб-камерой.',
  }
  requiredField: Обязательное поле для заполнения
  textDialog: Вы не ответили на все вопросы. Вы уверены что хотите закончить тестирование?
</i18n>
